import { keepPreviousData, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { endpoints, type CreditNoteListResponse } from "@/lib/endpoints";
import { queryKeys } from "@/lib/query-keys";

/**
 * The credit-note register (§17.3, section 34(1) CGST).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * A CREDIT NOTE **IS** A TAX DOCUMENT, and this is the one register where that
 * has to be said out loud, because the challan register beside it spends its
 * header saying the opposite about itself. The amount on a row is real output
 * tax being reversed, declared in the CDNR section of GSTR-1, and the customer
 * genuinely owes that much less. It is not a declared value in transit and it is
 * not an offer.
 *
 * IT IS ALSO POSITIVE. Nothing on this register carries a minus sign: the amount
 * is stored positive and the document TYPE means "this reduces" (§17.3). A row
 * printed as −₹2,340 would be the client inventing a sign the database does not
 * hold — and a sign is how a total somewhere silently changes direction.
 *
 * CANCELLED NOTES ARE VISIBLE BY DEFAULT, and that is the INVOICE rule rather
 * than the quotation/challan one — `creditNoteListWhere` applies no default
 * status filter at all. It is the right rule here for the reason it is right for
 * a bill: §34(1) requires a consecutive series, the number stays consumed, and a
 * number that simply vanishes from the register is a question from the
 * department that nobody can answer. A cancelled note credits nothing, which the
 * status badge says, and the money it released is already back on the bill.
 *
 * THE URL IS THE FILTER STATE, exactly as on the other three registers, and here
 * one link depends on it: `routes.creditNotesForInvoice(id)` is literally
 * `/credit-notes?invoiceId=7`, which is what the credited figure on a bill
 * opens. Component state would drop that on the first reload and show the whole
 * book to someone who asked about one invoice.
 *
 * `q` MATCHES THE NOTE'S OWN REFERENCES — `credit_note_number` AND the frozen
 * `original_invoice_number` — so typing a BILL number finds every note raised
 * against it. It does NOT match the customer name, which lives in
 * `customer_snapshot`, a text-backed JSON column the server will not query
 * inside (CLAUDE.md); that is what `customerId` is for.
 * ═════════════════════════════════════════════════════════════════════════════
 */

/** Cache keys for every credit-note query in the SPA. Declared in the shared factory. */
export const creditNoteKeys = queryKeys.creditNotes;

/**
 * Fifty a page — the same as the other three registers.
 *
 * The API's own default is 25 and its ceiling is 100
 * (`CREDIT_NOTE_LIST_DEFAULT_LIMIT` / `CREDIT_NOTE_LIST_MAX_LIMIT`), so `limit`
 * is always sent explicitly rather than left to the server. Sending 200 — the
 * INVOICE register's ceiling — is a 400 on this endpoint.
 */
export const CREDIT_NOTE_PAGE_SIZE = 50;

/**
 * The columns the API will sort by. A closed set: `CreditNoteListQuerySchema`
 * is `.strict()` and anything else is a 400, not an ignored parameter.
 */
export type CreditNoteSortKey = "number" | "date" | "customer" | "status" | "total";

const CREDIT_NOTE_SORT_KEYS: readonly CreditNoteSortKey[] = [
  "number",
  "date",
  "customer",
  "status",
  "total",
];

export interface CreditNoteFilters {
  /** `ISSUED` | `CANCELLED`. Absent shows both — see the header. */
  status?: string;
  /**
   * Every note against ONE bill (§34's link, from the other end).
   *
   * THIS IS THE FILTER THE INVOICE SCREEN LINKS IN WITH, and the same one the
   * card under the bill reads, so the figure and the list it opens are the same
   * query with the same key.
   */
  invoiceId?: number;
  customerId?: number;
  /**
   * Free text over the note's own number AND the frozen original invoice
   * number — so a BILL number finds its credit notes. Never the customer name.
   */
  q?: string;
  /** `YYYY-MM-DD`, INCLUSIVE, on `credit_note_date` — the NOTE's date. */
  from?: string;
  to?: string;
  /**
   * INTEGER PAISE, inclusive, on `grand_total_paise` — the amount CREDITED.
   *
   * Both bounds are non-negative, and here that is structural rather than
   * conventional: there is no negative amount anywhere in this table (§17.3), so
   * accepting one would offer a filter for rows that cannot exist.
   */
  minTotalPaise?: number;
  maxTotalPaise?: number;
  sort?: CreditNoteSortKey;
  dir?: "asc" | "desc";
  offset: number;
}

export function useCreditNoteFilters(): {
  filters: CreditNoteFilters;
  setFilters: (patch: Partial<CreditNoteFilters>) => void;
  clearFilters: () => void;
  activeCount: number;
} {
  const [params, setParams] = useSearchParams();

  const filters = useMemo<CreditNoteFilters>(() => {
    const status = params.get("status") ?? undefined;
    const q = params.get("q") ?? undefined;
    const rawOffset = params.get("offset");

    /** A junk `?customerId=abc` becomes "no filter" rather than a `NaN` in the query string. */
    const id = (raw: string | null): number | undefined => {
      if (raw === null || raw.trim() === "") return undefined;
      const value = Number(raw);
      return Number.isSafeInteger(value) && value > 0 ? value : undefined;
    };

    /** A bound that is not a whole number is no filter — never send `NaN`. */
    const paise = (raw: string | null): number | undefined => {
      if (raw === null || raw.trim() === "") return undefined;
      const value = Number(raw);
      return Number.isSafeInteger(value) && value >= 0 ? value : undefined;
    };

    return {
      status: status === "" ? undefined : status,
      q: q === "" ? undefined : q,
      invoiceId: id(params.get("invoiceId")),
      customerId: id(params.get("customerId")),
      from: params.get("from") ?? undefined,
      to: params.get("to") ?? undefined,
      minTotalPaise: paise(params.get("minTotalPaise")),
      maxTotalPaise: paise(params.get("maxTotalPaise")),
      // A hand-edited `?sort=whatever` degrades to no sort rather than a 400.
      sort: CREDIT_NOTE_SORT_KEYS.find((key) => key === params.get("sort")),
      dir: params.get("dir") === "asc" ? "asc" : params.get("dir") === "desc" ? "desc" : undefined,
      offset: rawOffset === null ? 0 : Math.max(0, Number(rawOffset) || 0),
    };
  }, [params]);

  const setFilters = useCallback(
    (patch: Partial<CreditNoteFilters>) => {
      const next = new URLSearchParams(params);
      for (const [key, value] of Object.entries(patch)) {
        if (value === undefined || value === "") next.delete(key);
        else next.set(key, String(value));
      }
      /*
       * ANY FILTER CHANGE RETURNS TO PAGE ONE. Narrowing the register while
       * still at offset 100 shows an empty page, which reads as "nothing
       * matches" when in fact three things do.
       */
      if (!("offset" in patch)) next.delete("offset");
      // `replace` so a filter fiddle does not stack twenty history entries
      // between the owner and the screen he came from.
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const clearFilters = useCallback(() => {
    setParams(new URLSearchParams(), { replace: true });
  }, [setParams]);

  const activeCount = [
    filters.status,
    filters.invoiceId,
    filters.customerId,
    filters.q,
    // A range counts once, however many ends it has — "2 filters" for one date
    // range would make the badge read as more than is actually on.
    filters.from ?? filters.to,
    filters.minTotalPaise ?? filters.maxTotalPaise,
  ].filter((value) => value !== undefined).length;

  return { filters, setFilters, clearFilters, activeCount };
}

/**
 * `GET /credit-notes` → `{ data, total, limit, offset }` — envelope key `data`,
 * like quotations and challans, UNLIKE `/invoices` which answers `invoices`.
 *
 * A 404 FROM THIS QUERY IS NOT "no such credit note". Every route in
 * `apps/api/src/routes/credit-notes.ts` sits behind `requireFlag("credit_notes")`,
 * which runs BEFORE `requireCap` and answers 404 rather than 403 so that
 * "forbidden" cannot leak the existence of unreleased work (§18.3). THE FLAG
 * SHIPS DISABLED, so today this is the ordinary answer, not an edge case. There
 * is no `useFlag` on the client and this hook does not invent one — `GET /flags`
 * needs `settings.write`, which the OWNER does not hold, so asking would 403 for
 * the one person using the app. The caller renders "not switched on"; never "you
 * do not have permission", which is precisely the leak the 404 avoids.
 */
export function useCreditNoteList(
  filters: CreditNoteFilters,
): UseQueryResult<CreditNoteListResponse> {
  const query = {
    status: filters.status,
    invoiceId: filters.invoiceId,
    customerId: filters.customerId,
    q: filters.q,
    from: filters.from,
    to: filters.to,
    // A key in `CreditNoteFilters` but not in THIS object is a filter that shows
    // in the URL and does nothing — the register would look narrowed and return
    // the whole book.
    minTotalPaise: filters.minTotalPaise,
    maxTotalPaise: filters.maxTotalPaise,
    limit: CREDIT_NOTE_PAGE_SIZE,
    sort: filters.sort,
    dir: filters.dir,
    offset: filters.offset === 0 ? undefined : filters.offset,
  };

  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) search.set(key, String(value));
  }
  const qs = search.toString();

  return useQuery({
    queryKey: creditNoteKeys.list(query),
    queryFn: () =>
      api.get<CreditNoteListResponse>(`${endpoints.creditNotes}${qs === "" ? "" : `?${qs}`}`),
    placeholderData: keepPreviousData,
    /*
     * Thirty seconds, as on the other registers. A credit note is immutable once
     * issued, but the LIST is not: one can be raised or cancelled on the shop
     * computer while this screen is open, and cancelling one changes what a
     * customer owes.
     */
    staleTime: 30_000,
  });
}

/**
 * The notes raised against ONE bill — the same endpoint, the same key, narrowed.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * IT IS NOT A SECOND ENDPOINT AND MUST NOT BECOME ONE. `GET /credit-notes?invoiceId=`
 * is what the register uses with the filter on, so the card under the invoice
 * and the list that card opens are the same query — and one write invalidates
 * both with a single prefix match on `["creditNotes", "list"]`.
 *
 * ISSUED ONLY IS *NOT* APPLIED HERE. The card shows cancelled notes too, because
 * a bill that was credited and then un-credited is a story the owner has to be
 * able to read on the bill itself; the CREDITED FIGURE beside it comes from the
 * server's `summary.creditedPaise`, which counts ISSUED rows only. Two different
 * questions — "what happened to this bill" and "how much is off it" — and the
 * client answers neither by summing rows.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function useCreditNotesForInvoice(
  invoiceId: number | undefined,
): UseQueryResult<CreditNoteListResponse> {
  const query = { invoiceId, limit: CREDIT_NOTE_PAGE_SIZE, sort: "date" as const, dir: "desc" as const };
  const search = new URLSearchParams({
    invoiceId: String(invoiceId ?? 0),
    limit: String(CREDIT_NOTE_PAGE_SIZE),
    sort: "date",
    dir: "desc",
  });

  return useQuery({
    queryKey: creditNoteKeys.list(query),
    queryFn: () =>
      api.get<CreditNoteListResponse>(`${endpoints.creditNotes}?${search.toString()}`),
    enabled: invoiceId !== undefined,
    /*
     * A 404 is the module switched off and a 403 is a capability the caller does
     * not hold; neither becomes true by asking again, and the card renders
     * nothing either way. Retrying would only delay the empty state.
     */
    retry: false,
    staleTime: 30_000,
  });
}
